import { useQuery } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import Button from '@/components/UI/Button';
import Callout from '@/components/UI/Callout';
import { StatList, StatRow } from '@/components/UI/StatRow';
import AlphaAtlasEntityBreakdownView from './AlphaAtlasEntityBreakdownView';
import {
    type AlphaAtlasDuration,
    type AlphaAtlasOutcomeKind,
    type AlphaAtlasReportRow,
    parseAlphaAtlasEntityKey,
    isVerificationBearingWindow
} from './AlphaAtlasReportState';
import { formatCount, formatPercentMicros, TONE_TEXT_CLASS, toneForMicros } from './alphaAtlasFormat';

interface AlphaAtlasEntityBreakdownProps {
    row: AlphaAtlasReportRow;
    durationId: AlphaAtlasDuration;
    outcomeKind: AlphaAtlasOutcomeKind;
    /** Requester-bound exposure, needed only when the row comes from a revealed window */
    exposureFingerprint: string | undefined;
    /** Limits the breakdown to these entities (the members of a bundle row) */
    entityKeys?: string[] | undefined;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}

/** Entity-scope rows are capped at the API's page limit; contributors sort first, so real coverage loss is rare. */
const ENTITY_PAGE_SIZE = 200;

/**
 * For a pooled row: the same hypothesis broken down by entity. For an entity row: the pooled
 * "all monitored entities" result it belongs to. Both read the same window, so they never need a
 * reveal the parent row did not already have.
 */
const AlphaAtlasEntityBreakdown = ({
    row,
    durationId,
    outcomeKind,
    exposureFingerprint,
    entityKeys,
    onSelectRow
}: AlphaAtlasEntityBreakdownProps) => {
    const isPooled = row.scope === 'pooled';
    const query = useQuery({
        ...trpc.alphaAtlas.reportResults.queryOptions({
            durationId,
            outcomeKind,
            windowRole: row.windowRole,
            scope: isPooled ? 'entity' : 'pooled',
            entities: isPooled && entityKeys && entityKeys.length > 0 ? entityKeys.map(parseAlphaAtlasEntityKey) : undefined,
            hypothesisFingerprint: row.hypothesisFingerprint,
            conditionKind: row.conditionKind,
            direction: row.direction,
            horizonSeconds: row.horizonSeconds,
            hideInsufficientSupport: false,
            sortBy: 'randomEdge',
            page: 1,
            pageSize: isPooled ? ENTITY_PAGE_SIZE : 1,
            exposureFingerprint: isVerificationBearingWindow(row.windowRole) ? exposureFingerprint : undefined
        }),
        staleTime: 60_000
    });

    if (query.error) {
        return (
            <Callout title={isPooled ? 'Could not load the entity breakdown' : 'Could not load the pooled result'} variant="error">
                {query.error.message}
            </Callout>
        );
    }
    if (query.isPending) {
        return (
            <div aria-busy="true" className="space-y-2">
                {Array.from({ length: 4 }, (_, index) => (
                    <div className="h-3 animate-pulse rounded bg-zinc-800" key={index} style={{ width: `${85 - index * 12}%` }} />
                ))}
            </div>
        );
    }

    if (isPooled)
        return (
            <AlphaAtlasEntityBreakdownView
                pooledRow={row}
                rows={query.data.items}
                total={entityKeys && entityKeys.length > 0 ? entityKeys.length : query.data.total}
                onSelectRow={onSelectRow}
            />
        );

    const pooled = query.data.items[0];
    if (!pooled) return <p className="text-sm text-zinc-500">No pooled result exists for this hypothesis in this window.</p>;
    return (
        <div className="space-y-2">
            <StatList>
                <StatRow
                    label="Net return, all entities"
                    value={
                        <span className={TONE_TEXT_CLASS[toneForMicros(pooled.netMeanMicros)]}>
                            {formatPercentMicros(pooled.netMeanMicros, { signed: true })}
                        </span>
                    }
                />
                <StatRow
                    label="Edge vs random, all entities"
                    value={
                        <span className={TONE_TEXT_CLASS[toneForMicros(pooled.randomEdgeMeanMicros)]}>
                            {formatPercentMicros(pooled.randomEdgeMeanMicros, { signed: true })}
                        </span>
                    }
                />
                <StatRow
                    label="Support, all entities"
                    value={`${formatCount(pooled.supportCount)} across ${formatCount(pooled.distinctEntityCount)} entities`}
                />
            </StatList>
            <Button size="sm" variant="outline-dark" onClick={() => onSelectRow(pooled)}>
                Open the pooled result
            </Button>
        </div>
    );
};

export default AlphaAtlasEntityBreakdown;
